import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Chip,
  Stack,
  CircularProgress,
  Avatar,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from "@mui/material";
import CustomButton from "../../components/common/CustomButton";
import { flowwiseApi } from "../../api/flowiseApi";
import { useNavigate } from "react-router-dom";
import VideoRecorderModal from "../../components/common/VideoModule";
import femaleInterviewer from "../../assets/images/FemaleRecruiter.png";
import { useAuth } from "../../contexts/AuthContext";
import useTerminateOnExit from "../../hooks/useTerminateOnExit";

const SpeechRecognition =
  window.SpeechRecognition || window.webkitSpeechRecognition || null;

// --- VOICE CONSTANTS ---
const ACCENT_OPTIONS = {
  "en-US": "US English",
  "en-GB": "UK English",
  "en-IN": "Indian English"
};

const VOICE_PRIORITIES = {
  'en-US': ['Google US English', 'Microsoft Zira', 'Microsoft David'],
  'en-GB': ['Google UK English Female', 'Google UK English Male', 'Microsoft Hazel'],
  'en-IN': ['Google Indian English', 'Microsoft Ravi']
};

const FlowiseInterview = () => {
  // --- SESSION IDs (required) ---
  const { upload_id } = useAuth();

  // --- system states ---
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  // const [notice, setNotice] = useState(null);
  const [selectedAccent, setSelectedAccent] = useState("en-US"); // ✅ added accent state

  // --- interview flow states ---
  const [startData, setStartData] = useState(null);
  const [questionData, setQuestionData] = useState(null);
  const [currentSkill, setCurrentSkill] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [stopInterview, setStopInterview] = useState("");
  const [skill_list, setSkillList] = useState("");
  const [expectedKeywords, setExpectedKeywords] = useState([]);
  const [failStreak, setFailStreak] = useState(0);
  const [followUpCount, setFollowUpCount] = useState(0);
  const [experienceYears, setExperienceYears] = useState(1);
  const [showVideoModal, setShowVideoModal] = useState(true);
  const [videoUrl, setVideoUrl] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);

  // --- advanced flow states (kept in sync with backend) ---
  // const [projectQuestionCount, setProjectQuestionCount] = useState(0);
  // const [previousQuestions, setPreviousQuestions] = useState([]);
  // const [lastQuestionType, setLastQuestionType] = useState("");

  // --- transcription / recording ---
  const [transcribedText, setTranscribedText] = useState("");
  const mediaRecorderRef = useRef(null);
  const audioChunks = useRef([]);
  const recognitionRef = useRef(null);

  const inactivityTimerRef = useRef(null);

  const currentSkillRef = useRef("");
  const failStreakRef = useRef(0);
  const followUpCountRef = useRef(0);
  const questionRef = useRef(null);
  const stopInterviewRef = useRef("");
  const heartbeatInterval = useRef(null);


  const INACTIVITY_TIMEOUT = 5 * 60 * 1000;

  // --- completion ---
  const [completed, setCompleted] = useState(false);
  const [completionReason, setCompletionReason] = useState("");
  const [finalScore, setFinalScore] = useState(null);

  const [confidenceScore, setConfidenceScore] = useState(null);
  const [communicationScore, setCommunicationScore] = useState(null);
  const [scoreHistory, setScoreHistory] = useState([]);

  const navigate = useNavigate();

  const [destroyVideoModule, setDestroyVideoModule] = useState(false);

  // --- UI snackbar ---
  const [snackbar, setSnackbar] = useState({ open: false, severity: "info", msg: "" });
  const handleSnackbar = (message, severity = "info") =>
    setSnackbar({ open: true, message, severity });

  // ---------------- START INTERVIEW ----------------
  const startInterview = async () => {
    setLoading(true);
    try {
      const response = await flowwiseApi.start();
      // console.log("Interview start response ::::::::::::", response);
      const { status, message, data, type } = response || {};

      if (status === 0 || type === "network_error") {
        handleSnackbar("Network error. Please check your connection.", "warning");
        return;
      }

      if (status === 400 || status === 401 || status === 500) {
        handleSnackbar(message || "Unable to start interview.", "warning");
        disarmBeacon(); // synchronously disables beacon before navigate
        navigate("/restriction", {
          state: { reason: "manually_terminated" },
          replace: true,
        });
        return;
      }

      if (!data) {
        handleSnackbar("Invalid interview start response.", "warning");
        return;
      }

      if (status === 200) {
        resetInactivityTimer();
        setStartData(data);

        const q = data.first_question ?? null;

        setQuestionData(q);
        setSkillList(data.current_skills || "");
        setCurrentSkill(data.current_skill || q?.skill || "");
        setDifficulty(data.difficulty || q?.difficulty || "");
        setExpectedKeywords(q?.expected_keywords || []);
        setExperienceYears(data.experience_years || 1);
        setFailStreak(data.fail_streak ?? 0);
        setFollowUpCount(data.follow_up_count ?? 0);

        handleSnackbar(message || "Interview started successfully!", "success");
      }
    } catch (err) {
      console.error("Start Error:", err);
      setSnackbar({ open: true, severity: "error", msg: "Start failed" });
    } finally {
      setLoading(false);
    }
  };

  // --- VOICE SELECTION HELPER ---
  const selectOptimalVoice = (accentCode) => {
    const synth = window.speechSynthesis;
    const voices = synth.getVoices();
    const exactMatches = voices.filter(voice => voice.lang === accentCode);

    if (VOICE_PRIORITIES[accentCode] && exactMatches.length > 0) {
      for (const preferredName of VOICE_PRIORITIES[accentCode]) {
        const voice = exactMatches.find(v => v.name.includes(preferredName));
        if (voice) return voice;
      }
    }

    return exactMatches[0] || voices.find(v => v.lang.startsWith(accentCode.split('-')[0])) || voices[0];
  };


  const { disarm: disarmBeacon } = useTerminateOnExit({
    isActive: !completed,
    endpoint: `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview/violation/`,
  });
  // Trigger speech whenever a new question arrives
  useEffect(() => {
    if (!window.speechSynthesis) return;

    const hour = new Date().getHours();
    let greeting = "Good Evening";

    if (hour < 12) {
      greeting = "Good Morning";
    } else if (hour < 17) {
      greeting = "Good Afternoon";
    }

    const message = `
    ${greeting}.
    Welcome to your AI interview.
    Whenever you're comfortable,
    click Begin Interview to get started.
  `;

    const speak = () => {
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(message);
      utterance.rate = 0.92;
      utterance.pitch = 1;
      utterance.volume = 1;

      // For the initial greeting, we default to the best available female or English voice
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice =
        voices.find((v) => v.name.toLowerCase().includes("female")) ||
        voices.find((v) => v.lang.toLowerCase().includes("en")) ||
        voices[0];

      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }

      window.speechSynthesis.speak(utterance);
    };

    // Chrome voice loading fix
    if (window.speechSynthesis.getVoices().length > 0) {
      speak();
    } else {
      window.speechSynthesis.onvoiceschanged = speak;
    }

    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  const speakQuestion = (text) => {
    if (!text) return;

    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(true);

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = selectedAccent;
    utterance.rate = 0.9;
    utterance.pitch = 1;

    const selectedVoice = selectOptimalVoice(selectedAccent);
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    window.speechSynthesis.speak(utterance);
  };

  // Added selectedAccent to dependencies so it reads the question again if accent changes
  useEffect(() => {
    if (questionData?.question) {
      speakQuestion(questionData.question);
      resetInactivityTimer();
    }
  }, [questionData, selectedAccent]);


  useEffect(() => {
    const handleBack = () => {
      window.history.pushState(null, "", window.location.href);
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handleBack);

    return () => {
      window.removeEventListener("popstate", handleBack);
    };
  }, []);

  useEffect(() => {
    currentSkillRef.current = currentSkill;
  }, [currentSkill]);

  useEffect(() => {
    stopInterviewRef.current = stopInterview;
  }, [stopInterview]);

  useEffect(() => {
    failStreakRef.current = failStreak;
  }, [failStreak]);

  useEffect(() => {
    followUpCountRef.current = followUpCount;
  }, [followUpCount]);

  useEffect(() => {
    questionRef.current = questionData;
  }, [questionData]);

  useEffect(() => {
    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "click",
      "touchstart",
    ];

    const activityHandler = () => {
      resetInactivityTimer();
    };

    events.forEach((event) => {
      window.addEventListener(event, activityHandler);
    });

    resetInactivityTimer();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, activityHandler);
      });

      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, []);


  const handleInactiveCandidate = async () => {
    try {
      const payload = {
        "current_skill": currentSkillRef.current,
        "fail_streak": failStreakRef.current,
        "follow_up_count": followUpCountRef.current,
        "upload_id": upload_id
      }
      await flowwiseApi.inactive(payload);
      // console.log(response)
      disarmBeacon(); // synchronously disables beacon before navigate
      navigate("/restriction", {
        state: {
          reason: "inactive_long_time",
        },
        replace: true,
      });
      // console.log("Candidate marked inactive");
    } catch (err) {
      console.error("Inactive API failed", err);
    }
  };

  const sendHeartbeat = async () => {
    try {
      const payload = {
        current_skill: currentSkillRef.current,
        fail_streak: failStreakRef.current,
        follow_up_count: followUpCountRef.current,
        isFinish: stopInterview.current,
        upload_id: upload_id
      };

      // console.log(payload)

      await flowwiseApi.heartbeat(payload);

    } catch (error) {
      console.error("Heartbeat failed:", error);
    }
  };

  useEffect(() => {
    sendHeartbeat();

    heartbeatInterval.current = setInterval(sendHeartbeat, 15000);

    return () => {
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
      }
    };
  }, []);

  const resetInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      handleInactiveCandidate();
    }, INACTIVITY_TIMEOUT);
  };

  // ---------------- RECORDING / TRANSCRIPTION ----------------
  const startRecording = async () => {
    try {
      setRecording(true);
      resetInactivityTimer();
      audioChunks.current = [];
      setAudioBlob(null);
      setTranscribedText("");

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorder.ondataavailable = (e) => audioChunks.current.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunks.current, { type: "audio/mp3" });
        setAudioBlob(blob);
      };
      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;

      // start speech recognition if available
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = selectedAccent; // ✅ matched transcription to the selected accent

        recognition.onresult = (event) => {
          let text = "";
          for (let i = 0; i < event.results.length; i++) {
            text += event.results[i][0].transcript + " ";
          }
          setTranscribedText(text.trim());
        };
        recognition.onerror = (e) => {
          console.warn("Speech recognition error:", e);
        };
        recognition.start();
        recognitionRef.current = recognition;
      }
    } catch (err) {
      console.error("Recording start failed", err);
      setSnackbar({ open: true, severity: "error", msg: "Unable to start recording" });
      setRecording(false);
    }
  };

  const stopRecording = () => {
    resetInactivityTimer();
    setRecording(false);
    if (mediaRecorderRef.current) {
      try {
        mediaRecorderRef.current.stop();
      } catch (err) {
        console.warn("mediaRecorder.stop error:", err);
      }
    }
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (err) {
        // ignore
      }
    }
  };

  if (destroyVideoModule) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <Typography variant="h6">Finalizing interview...</Typography>
      </Box>
    );
  }

  // ---------------- SUBMIT ANSWER ----------------
  const submitAnswer = async () => {
    if (!audioBlob) {
      setSnackbar({ open: true, severity: "warning", msg: "Please record your answer first." });
      return;
    }
    if (!questionData) {
      setSnackbar({ open: true, severity: "warning", msg: "No active question." });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();

      formData.append("previous_question", questionData.question || "");
      formData.append("expected_keywords", JSON.stringify(expectedKeywords || []));
      formData.append("candidate_answer", transcribedText || "");
      formData.append("current_skill", currentSkill || "");
      formData.append("current_difficulty", difficulty || "");
      formData.append("fail_streak", failStreak ?? 0);
      formData.append("follow_up_count", followUpCount ?? 0);
      formData.append("experience_years", experienceYears || 1);
      formData.append("ideal_answer", questionData?.ideal_answer || "");

      if (startData?.skills_list) {
        formData.append("skills", startData.skills_list);
      }

      if (startData?.jd_summary) formData.append("jd_parsed", startData.jd_summary);
      if (startData?.resume_summary) formData.append("resume_parsed", startData.resume_summary);

      formData.append("audio", audioBlob, "response.mp3");

      const response = await flowwiseApi.process(formData, { headers: { "Content-Type": "multipart/form-data" } });
      // console.log("Submit response ::::::::::::", response);
      const { status, message, data, type } = response || {};

      if (status === 0 || type === "network_error") {
        setSnackbar({ open: true, severity: "warning", msg: "Network error. Please try again." });
        return;
      }

      if (status === 400 || status === 401 || status === 500 || !data) {
        setSnackbar({ open: true, severity: "warning", msg: message || "Submit failed" });
        return;
      }

      const respData = data;
      if (!respData) throw new Error("Invalid submit response");

      setStopInterview(respData.stop_interview)

      if (respData.stop_interview) {
        stopInterviewRef.current = true;
        setStopInterview(true);
        try {
          const endForm = new FormData();
          endForm.append("upload_id", upload_id);

          if (heartbeatInterval.current) {
            clearInterval(heartbeatInterval.current);
          }

          const endRes = await flowwiseApi.end(endForm);
          const { status: endStatus, message: endMessage, data: endData, type: endType } = endRes || {};

          if (endStatus === 0 || endType === "network_error") {
            setFinalScore(respData.score ?? null);
          } else if (endStatus === 200 && endData) {
            setFinalScore(endData.score ?? respData.score ?? null);
            setCompleted(true);


          }

          setCompletionReason(respData.reason || endMessage || "Interview completed");
        } catch (endErr) {
          console.error("End API failed:", endErr);
          setFinalScore(respData.score ?? null);
          setCompletionReason(respData.reason || "Interview completed");
        }

        window.onbeforeunload = null;
        setCompleted(true);
        setQuestionData(null);
        setSnackbar({ open: true, severity: "success", msg: "Interview completed" });
        return;
      }

      const nextQ = respData.question || null;
      setQuestionData(nextQ);
      setCurrentSkill(respData.next_skill || nextQ?.skill || currentSkill);
      setDifficulty(respData.next_difficulty || nextQ?.difficulty || difficulty);
      setFailStreak(respData.fail_streak ?? 0);
      setFollowUpCount(respData.follow_up_count ?? 0);
      setExpectedKeywords(nextQ?.expected_keywords || []);

      const conf = respData.confidence_score ?? null;
      const comm = respData.communication_score ?? null;
      setConfidenceScore(conf);
      setCommunicationScore(comm);

      setScoreHistory(prev => [
        ...prev,
        {
          question: questionData?.question,
          score: respData.score ?? null,
          confidence_score: conf,
          communication_score: comm,
        }
      ]);

      setAudioBlob(null);
      setTranscribedText("");
      resetInactivityTimer();
      setSnackbar({ open: true, severity: "success", msg: "Answer submitted" });

    } catch (err) {
      console.error("Submit error:", err);
      setSnackbar({ open: true, severity: "error", msg: "Submit failed" });
    } finally {
      setLoading(false);
    }
  };

  const renderCompletion = () => (
    navigate('/success', {
      state: {
        reason: 'interview_completed',
        message: 'Your interview was submitted successfully!'
      },
      replace: true
    })
  )

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", bgcolor: "#ffffff" }}>
      {/* Professional Header */}
      <Box sx={{
        bgcolor: "#1a237e",
        px: 3,
        py: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "white", letterSpacing: 0.5 }}>
          AI Interview Platform
        </Typography>
      </Box>

      {/* Draggable Video Module */}
      {!destroyVideoModule && videoUrl && (
        <Box
          id="draggable-video"
          sx={{
            position: "fixed",
            top: 56,
            right: 20,
            width: 220,
            height: 100,
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
            zIndex: 1000,
            bgcolor: "black",
            border: "3px solid #1a237e",
            cursor: "move",
            transition: "box-shadow 0.2s",
            "&:hover": {
              boxShadow: "0 12px 32px rgba(0,0,0,0.35)"
            }
          }}
          draggable
          onDragEnd={(e) => {
            const box = e.currentTarget;
            const newX = Math.max(0, Math.min(e.clientX - 140, window.innerWidth - 280));
            const newY = Math.max(0, Math.min(e.clientY - 100, window.innerHeight - 200));
            box.style.left = `${newX}px`;
            box.style.top = `${newY}px`;
            box.style.right = "auto";
          }}
        >
          <video
            src={videoUrl}
            autoPlay
            muted
            loop
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <Box sx={{
            position: "absolute",
            top: 6,
            right: 6,
            bgcolor: "rgba(0,0,0,0.6)",
            color: "white",
            px: 0.8,
            py: 0.3,
            borderRadius: 1,
            fontSize: "0.7rem"
          }}>
            🔴 LIVE
          </Box>
        </Box>
      )}

      {!destroyVideoModule && !videoUrl && (
        <VideoRecorderModal
          open={showVideoModal}
          onBeforeNavigate={disarmBeacon}
          onClose={() => setShowVideoModal(false)}
          onVideoReady={(url) => setVideoUrl(url)}
        />
      )}

      {completed ? (
        renderCompletion()
      ) : !questionData ? (
        /* WELCOME SCREEN */
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background:
              "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
            p: 2,
          }}
        >
          <Card
            sx={{
              maxWidth: 500,
              width: "100%",
              p: { xs: 3, sm: 4 },
              textAlign: "center",
              borderRadius: 4,
              boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
              backdropFilter: "blur(10px)",
            }}
          >
            {/* Avatar */}
            <Avatar
              src={femaleInterviewer}
              sx={{
                width: 120,
                height: 120,
                mx: "auto",
                mb: 3,
                border: "4px solid #1a237e",
                boxShadow: "0 6px 20px rgba(26,35,126,0.25)",
              }}
            />

            {/* Dynamic Greeting */}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#1a237e",
                mb: 1,
              }}
            >
              {(() => {
                const hour = new Date().getHours();

                if (hour < 12) return "Good Morning";
                if (hour < 17) return "Good Afternoon";
                return "Good Evening";
              })()}
            </Typography>

            {/* Title */}
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: "#1a237e",
                mb: 2,
              }}
            >
              Welcome to Your AI Interview
            </Typography>

            {/* Short Message */}
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mb: 4,
                lineHeight: 1.7,
                fontSize: "0.98rem",
              }}
            >
              Whenever you're comfortable, click{" "}
              <strong>Begin Interview</strong> to get started.
            </Typography>

            {/* Button */}
            <CustomButton
              variant="contained"
              size="large"
              onClick={startInterview}
              disabled={loading}
              sx={{
                px: 5,
                py: 1.6,
                borderRadius: 3,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 700,
                minWidth: 220,
                background:
                  "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
                boxShadow: "0 6px 20px rgba(26,35,126,0.35)",
                transition: "all 0.25s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 10px 24px rgba(26,35,126,0.45)",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Begin Interview"
              )}
            </CustomButton>
          </Card>
        </Box>
      ) : (
        /* INTERVIEW INTERFACE */
        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Left Panel - Question */}
          <Box sx={{
            width: { xs: "100%", md: "50%" },
            display: "flex",
            flexDirection: "column",
            bgcolor: "#fafbfc",
            borderRight: "1px solid #e1e4e8"
          }}>
            <Box sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}>
              {/* Interviewer Header */}
              <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 3,
                pb: 2,
                borderBottom: "2px solid #e1e4e8"
              }}>
                <Avatar
                  src={femaleInterviewer}
                  sx={{
                    width: 60,
                    height: 60,
                    border: "2px solid #1a237e"
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1a237e", mb: 0.5 }}>
                    AI Interviewer
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Chip
                      label={currentSkill}
                      size="small"
                      sx={{
                        bgcolor: "#1a237e",
                        color: "white",
                        fontWeight: 600,
                        fontSize: "0.75rem"
                      }}
                    />
                    <Chip
                      label={difficulty}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: "#1a237e",
                        color: "#1a237e",
                        fontWeight: 600,
                        fontSize: "0.75rem"
                      }}
                    />
                  </Stack>
                </Box>

                {/* ✅ NEW ACCENT SELECTOR */}
                <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'white', borderRadius: 1 }}>
                  <InputLabel id="accent-select-label">Voice Accent</InputLabel>
                  <Select
                    labelId="accent-select-label"
                    value={selectedAccent}
                    label="Voice Accent"
                    onChange={(e) => setSelectedAccent(e.target.value)}
                  >
                    {Object.entries(ACCENT_OPTIONS).map(([code, name]) => (
                      <MenuItem key={code} value={code}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <CustomButton
                  variant="outlined"
                  size="small"
                  onClick={() => speakQuestion(questionData?.question)}
                  disabled={loading}
                  sx={{
                    minWidth: "auto",
                    p: 1,
                    borderRadius: 1.5,
                    fontSize: "1rem",
                  }}
                >
                  🔊
                </CustomButton>
              </Box>

              {/* Question Card */}
              <Box sx={{
                flex: 1,
                bgcolor: "white",
                borderRadius: 2,
                p: 3,
                mb: 3,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                border: "1px solid #e1e4e8",
                overflow: "auto"
              }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "1rem",
                    lineHeight: 1.7,
                    color: "#24292e"
                  }}
                >
                  {questionData?.question}
                </Typography>
              </Box>

              {/* Action Buttons */}
              <Stack spacing={1.5}>
                {!recording ? (
                  <CustomButton
                    variant="contained"
                    size="large"
                    startIcon={<span style={{ fontSize: "1.1rem" }}>🎤</span>}
                    onClick={startRecording}
                    disabled={isSpeaking || loading}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontSize: "0.95rem",
                      fontWeight: 600,
                    }}
                  >
                    Start Answer
                  </CustomButton>
                ) : (
                  <CustomButton
                    variant="outlined"
                    size="large"
                    startIcon={<span>⏹</span>}
                    onClick={stopRecording}
                    disabled={loading}
                    sx={{

                      borderWidth: 2,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontSize: "0.95rem",
                      fontWeight: 600,
                    }}
                  >
                    Stop Recording
                  </CustomButton>
                )}

                <CustomButton
                  variant="contained"
                  size="large"
                  onClick={submitAnswer}
                  disabled={loading || !audioBlob || isSpeaking}
                  sx={{

                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: "0.95rem",
                    fontWeight: 600,
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Submit & Next Question"}
                </CustomButton>
              </Stack>
            </Box>
          </Box>

          {/* Right Panel - Transcript */}
          <Box sx={{
            width: { xs: "100%", md: "50%" },
            display: "flex",
            flexDirection: "column",
            bgcolor: "#ffffff"
          }}>
            <Box sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}>

              <Card sx={{
                p: 2.5,
                bgcolor: recording ? "#ffebee" : audioBlob ? "#e8f5e9" : "#e3f2fd",
                border: `2px solid ${recording ? "#d32f2f" : audioBlob ? "#4caf50" : "#2196f3"}`,
                borderRadius: 2,
                boxShadow: "none",
                mb: 3
              }}>
                <Typography
                  variant="caption"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    color: recording ? "#d32f2f" : audioBlob ? "#2e7d32" : "#1976d2",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    mb: 0.5,
                    display: "block"
                  }}
                >
                  Microphone Status
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                  {recording ? "🔴 Recording in Progress..." : audioBlob ? "✅ Audio Ready to Submit" : "⏸️ Standby Mode"}
                </Typography>
              </Card>
              {/* Transcript Header */}
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  color: "#1a237e",
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  fontSize: "0.9rem"
                }}
              >
                Live Transcript
              </Typography>

              {/* Transcript Box */}
              <Box sx={{
                flex: 1,
                bgcolor: "#f6f8fa",
                borderRadius: 2,
                p: 2.5,
                overflow: "auto",
                border: "2px solid #e1e4e8",
                mb: 2.5,
                fontFamily: "monospace"
              }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: transcribedText ? "#24292e" : "#6a737d",
                    fontSize: "0.9rem",
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap"
                  }}
                >
                  {transcribedText || "🎧 Listening for your voice..."}
                </Typography>
              </Box>

              {/* Status Card */}

            </Box>
          </Box>
        </Box>
      )}

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{
            fontSize: "0.9rem",
            fontWeight: 600,
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)"
          }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FlowiseInterview;